import React from "react";
import { Css, increment, Palette, type Margin, type Only, type Position, type Properties, type Xss } from "src/Css";
import { useTestIds } from "src/hooks";

export type ButtonXss = Xss<
  | Margin
  | Position
  | "backgroundColor"
  | "borderColor"
  | "borderRadius"
  | "borderWidth"
  | "color"
  | "display"
  | "height"
  | "minHeight"
  | "minWidth"
  | "padding"
  | "textTransform"
  | "width"
>;

/** Enable outline styles for buttons */
type Outline = { outline?: boolean };

// Base button interface which includes all HTML attributes and Button attributes
interface BaseButtonProps<X extends Only<ButtonXss, X>>
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className" | "color" | "css"> {
  xss?: X;
  testid?: string;
  href?: HTMLAnchorElement["href"];
  target?: HTMLAnchorElement["target"];
  rel?: HTMLAnchorElement["rel"];
  "data-testid"?: string;
}

// Button props interface which only exposes used button props
export interface ButtonProps<X extends Only<ButtonXss, X>> extends BaseButtonProps<X> {
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

export interface DestructiveButtonProps<X extends Only<ButtonXss, X>> extends ButtonProps<X> {
  destructive?: boolean;
}

export interface OutlineButtonProps<X extends Only<ButtonXss, X>> extends DestructiveButtonProps<X>, Outline {
  // Determine how wide the button is, using narrow renders a button not as wide as "wide" button
  size?: "wide" | "narrow";
}

export interface TextButtonProps<X extends Only<ButtonXss, X>> extends DestructiveButtonProps<X>, Outline {}

export interface IconButtonProps<X extends Only<ButtonXss, X>> extends BaseButtonProps<X>, Outline {
  variant?: "default" | "secondary";
  // Render the small version of the icon button
  small?: boolean;
}

type RawButtonProps = ButtonProps<ButtonXss> & { variantCss: Properties };

const RawButton = React.forwardRef<HTMLButtonElement, RawButtonProps>(function RawButton(props, ref) {
  const {
    xss,
    onClick,
    children,
    testid,
    href,
    target,
    rel,
    variantCss,
    disabled,
    endIcon,
    fullWidth,
    startIcon,
    type,
    ...otherProps
  } = props;
  const buttonCss = { ...buttonBaseCss, ...variantCss, ...(fullWidth ? Css.w100.$ : {}), ...xss };
  const text = typeof children === "string" ? `${children.replace(/\W/g, "").toLowerCase()}Button` : "button";
  const [testId] = useTestIds(testid || text);
  const [buttonRef, handleClick] = useDisableClickFocus(ref, onClick);
  const content = renderButtonContent({ children, endIcon, startIcon });

  if (href) {
    const linkProps = otherProps as Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "color">;

    return (
      <a
        css={buttonCss}
        {...{ href: disabled ? undefined : href, target, rel, ...testId, ...linkProps }}
        aria-disabled={disabled || undefined}
        onClick={(event) => {
          if (disabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          handleClick(event as unknown as React.MouseEvent<HTMLButtonElement>);
        }}
        ref={buttonRef as React.ForwardedRef<HTMLAnchorElement>}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={buttonRef}
      css={buttonCss}
      disabled={disabled}
      onClick={handleClick}
      type={type ?? "button"}
      {...{ ...testId, ...otherProps }}
    >
      {content}
    </button>
  );
});

/** Primary button with Homebound button styles. */
export const PrimaryButton = React.forwardRef<HTMLButtonElement, ButtonProps<ButtonXss>>((props, ref) => {
  return <RawButton {...props} ref={ref} variantCss={primaryButtonCss} />;
});

// TODO: SecondaryButton should somehow be incorporated into a new button component as we remove MUI
/** Secondary button with Homebound button styles. */
export const SecondaryButton = React.forwardRef<HTMLButtonElement, ButtonProps<ButtonXss>>((props, ref) => {
  return <RawButton {...props} ref={ref} variantCss={secondaryButtonCss} />;
});

/** Outline button with Homebound button styles. This button handles a destructive state as well. */
export const OutlineButton = React.forwardRef<HTMLButtonElement, OutlineButtonProps<ButtonXss>>((props, ref) => {
  const { destructive, size, ...otherProps } = props;

  return (
    <RawButton
      {...otherProps}
      ref={ref}
      variantCss={{
        ...outlineButtonCss,
        ...(destructive ? outlineDestructiveButtonCss : {}),
        ...(size === "narrow" ? outlineNarrowButtonCss : {}),
      }}
    />
  );
});

/**
 * Text button with Homebound button styles.
 * This button handles a destructive state as well.
 */
export const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps<ButtonXss>>((props, ref) => {
  const { destructive, outline, ...otherProps } = props;
  return (
    <RawButton
      {...otherProps}
      ref={ref}
      variantCss={{
        ...textButtonCss,
        ...(outline ? textButtonOutlineCss : {}),
        ...(destructive ? textDestructiveButtonCss : {}),
      }}
    />
  );
});

type RawIconButtonProps = IconButtonProps<ButtonXss> & { variantCss: Properties };

const RawIconButton = React.forwardRef<HTMLButtonElement, RawIconButtonProps>(function RawIconButton(props, ref) {
  const { xss, onClick, children, testid, href, target, rel, variant, variantCss, disabled, type, ...otherProps } =
    props;

  const buttonCss = { ...buttonBaseCss, ...variantCss, ...(variant === "secondary" ? secondaryButtonCss : {}), ...xss };
  const [buttonRef, handleClick] = useDisableClickFocus(ref, onClick);
  const [testId] = useTestIds(props["data-testid"] || testid || "iconButton");

  if (href) {
    const linkProps = otherProps as Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "color">;

    return (
      <a
        css={buttonCss}
        {...{ href: disabled ? undefined : href, target, rel, ...testId, ...linkProps }}
        aria-disabled={disabled || undefined}
        onClick={(event) => {
          if (disabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          handleClick(event as unknown as React.MouseEvent<HTMLButtonElement>);
        }}
        ref={buttonRef as React.ForwardedRef<HTMLAnchorElement>}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      ref={buttonRef}
      css={buttonCss}
      disabled={disabled}
      onClick={handleClick}
      type={type ?? "button"}
      {...{ ...otherProps, ...testId }}
    >
      {children}
    </button>
  );
});

/**
 * Icon button component which accepts a MUI or React.Node icon child.
 *
 * TODO: Future enhancement
 * - Color prop for easy icon styling
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps<ButtonXss>>((props, ref) => {
  const { outline, small, ...otherProps } = props;
  return (
    <RawIconButton
      {...otherProps}
      ref={ref}
      variantCss={{ ...iconButtonCss, ...(outline ? iconButtonOutlineCss : {}), ...(small ? iconButtonSmallCss : {}) }}
    />
  );
});

const buttonBaseCss = Css.dif.aic.jcc.relative.m0.px7.py1.t12up.cursorPointer.brPill
  .hPx(34)
  .outline0.shadowNone.bw1.bss.bcTransparent.bgTransparent.tdn.vam.usn.add("appearance", "none")
  .add("WebkitAppearance", "none")
  .add("MozAppearance", "none")
  .add(
    "transition",
    "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  )
  .when({
    ":hover": Css.shadowNone.tdn.$,
    ":active": Css.shadowNone.tdn.$,
    ":focus": Css.shadowNone.bsDashed.$,
    ":disabled": Css.add("cursor", "not-allowed").add("pointerEvents", "auto").$,
    "[aria-disabled=true]": Css.add("cursor", "not-allowed").add("pointerEvents", "auto").$,
  }).$;

const primaryButtonCss = Css.bgBlack.bcTransparent.white.when({
  ":hover": Css.bgBlackFaded.bcTransparent.$,
  ":active": Css.bgBlackFaded.bcTransparent.$,
  ":focus": Css.bcWhite.$,
  ":disabled": Css.bgGray400.$,
  "[aria-disabled=true]": Css.bgGray400.$,
}).$;

const secondaryButtonCss = Css.bgWhite.bcTransparent.black.bss.when({
  ":hover": Css.bgWhitePure.bcGray700.$,
  ":active": Css.bgWhitePure.bcGray700.$,
  ":focus": Css.bss.bcBlack.$,
  ":disabled": Css.bgGray400.$,
  "[aria-disabled=true]": Css.bgGray400.$,
}).$;

const outlineButtonCss = Css.pxPx(increment(7) - 2)
  .pyPx(increment(1) - 2)
  .black.bgWhite.bss.bcBlack.bw2.when({
    ":hover": Css.bgTransparentGray.bcBlack.$,
    ":active": Css.bgTransparentGray.bcBlack.$,
    ":focus": Css.$,
    ":disabled": Css.gray400.bcGray400.$,
    "[aria-disabled=true]": Css.gray400.bcGray400.$,
  }).$;

const outlineDestructiveButtonCss = Css.bgRed.bcRed.white.when({
  ":hover": Css.bgRedFaded.bcTransparent.$,
  ":active": Css.bgRedFaded.bcTransparent.$,
  ":focus": Css.bcWhite.$,
  ":disabled": Css.gray600.bcGray400.bgRedFadedTransparent.$,
  "[aria-disabled=true]": Css.gray600.bcGray400.bgRedFadedTransparent.$,
}).$;

const outlineNarrowButtonCss = Css.px5.$;

const textButtonCss = Css.px2.black.bgTransparent.bsn.when({
  ":hover": Css.bgTransparentGray.bcTransparent.$,
  ":active": Css.bgTransparentGray.bcTransparent.$,
  ":focus": Css.bcGray600.bgTransparentGray.$,
  ":disabled": Css.gray400.bgLightTransparentGray.add("stroke", Palette.Gray400).$,
  "[aria-disabled=true]": Css.gray400.bgLightTransparentGray.add("stroke", Palette.Gray400).$,
}).$;

const textButtonOutlineCss = Css.bss.bw1.bcGray400.$;

const textDestructiveButtonCss = Css.red.when({
  ":disabled": Css.redFaded.$,
  "[aria-disabled=true]": Css.redFaded.$,
}).$;

const iconButtonCss = Css.pPx(increment(1) - 2)
  .br100.bsn.black.bgTransparent.mwPx(40)
  .mhPx(40)
  .when({
    ":hover": Css.bgTransparentGray.bcTransparent.$,
    ":active": Css.bgTransparentGray.bcTransparent.$,
    ":focus": Css.bgTransparentGray.bcGray600.bsDashed.$,
    ":disabled": Css.bgTransparentGray.gray400.$,
    "[aria-disabled=true]": Css.bgTransparentGray.gray400.$,
  }).$;

const iconButtonOutlineCss = Css.bss.bcGray400.$;

const iconButtonSmallCss = Css.mw("auto").mh("auto").sqPx(35).$;

const buttonLabelCss = Css.w100.display("inherit").ai("inherit").jc("inherit").$;

const buttonStartIconCss = Css.dif.aic.mr1.$;

const buttonEndIconCss = Css.dif.aic.ml1.$;

/** Hook which disabled the focus state when clicking on a button */
function useDisableClickFocus(
  ref: React.ForwardedRef<HTMLButtonElement>,
  onClick: React.MouseEventHandler<HTMLButtonElement> | undefined,
): readonly [React.ForwardedRef<HTMLButtonElement>, React.MouseEventHandler<HTMLButtonElement>] {
  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    // https://github.com/Microsoft/TypeScript/issues/5901#issuecomment-431649653
    if (ref && typeof ref !== "function" && ref.current instanceof HTMLElement) {
      ref.current.blur();
    }

    onClick?.(event);
  }

  return [ref, handleClick] as const;
}

function renderButtonContent(props: Pick<ButtonProps<ButtonXss>, "children" | "endIcon" | "startIcon">) {
  const { children, endIcon, startIcon } = props;

  return (
    <span css={buttonLabelCss}>
      {startIcon && <span css={buttonStartIconCss}>{startIcon}</span>}
      {children}
      {endIcon && <span css={buttonEndIconCss}>{endIcon}</span>}
    </span>
  );
}
